import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MapPin, Clock, Send, PhoneCall } from 'lucide-react';

const CONTACT_INFO = [
  {
    Icon: Mail, color: 'text-brand-indigo', bg: 'bg-brand-indigo/15', border: 'border-brand-indigo/20',
    title: 'Email Us',
    detail: 'info@meenamma-trust.org',
    href: 'mailto:info@meenamma-trust.org',
  },
  {
    Icon: MapPin, color: 'text-brand-emerald', bg: 'bg-brand-emerald/15', border: 'border-brand-emerald/20',
    title: 'Our Location',
    detail: 'Vijaya Nagar Colony, Srikakulam\nAndhra Pradesh 532001, India',
  },
  {
    Icon: Clock, color: 'text-brand-amber', bg: 'bg-brand-amber/15', border: 'border-brand-amber/20',
    title: 'Office Hours',
    detail: 'Mon–Fri: 9 AM–6 PM\nSat: 10 AM–4 PM\nSun: Closed',
  },
];

const INITIAL = { name: '', email: '', subject: '', message: '' };

const ContactSection = () => {
  const [formData, setFormData] = useState(INITIAL);

  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! We will get back to you soon.');
    setFormData(INITIAL);
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-slate-900 text-white relative overflow-hidden">

      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-brand-indigo/8 blur-[80px]" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full bg-brand-emerald/8 blur-[80px]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">

        {/* Badge + Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full
                             bg-white/8 border border-white/12 text-white/70
                             text-xs font-bold tracking-widest uppercase">
              <PhoneCall className="h-3 w-3" />
              Reach Out
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Get In{' '}
            <span className="bg-gradient-to-r from-brand-indigo to-brand-emerald bg-clip-text text-transparent">
              Touch
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Interested in supporting education? We welcome inquiries about collaboration,
            volunteering, or general questions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-5xl mx-auto">

          {/* Contact info cards */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            {CONTACT_INFO.map(({ Icon, color, bg, border, title, detail, href }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.45 }}
                className="flex gap-4 items-start p-5 rounded-2xl bg-white/5 border border-white/8
                           hover:bg-white/8 hover:border-white/12 transition-all duration-200 group"
              >
                <div className={`p-3 rounded-xl ${bg} border ${border} flex-shrink-0
                                 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm mb-1">{title}</p>
                  {href ? (
                    <a href={href} className="text-slate-400 hover:text-white transition-colors text-sm leading-relaxed">
                      {detail}
                    </a>
                  ) : (
                    <p className="text-slate-400 text-sm whitespace-pre-line leading-relaxed">{detail}</p>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Decorative quote */}
            <motion.div
              className="mt-6 p-6 rounded-2xl border border-white/6 bg-gradient-to-br from-brand-indigo/10 to-brand-emerald/8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-slate-300 text-sm italic leading-relaxed">
                "Education is the most powerful weapon which you can use to change the world."
              </p>
              <p className="text-slate-500 text-xs mt-2 font-medium">— Nelson Mandela</p>
            </motion.div>
          </motion.div>

          {/* Contact form */}
          <motion.div
            className="bg-white/6 backdrop-blur-md rounded-2xl border border-white/10 p-8 shadow-2xl"
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <h3 className="text-xl font-bold mb-1.5">Connect With Us</h3>
            <p className="text-slate-400 text-sm mb-7">Fill in the form and we'll respond within 24 hours.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-white/10 border-white/15 text-white placeholder:text-slate-500
                             focus-visible:ring-white/20 focus-visible:border-white/30
                             hover:border-white/25 h-11"
                  required
                />
                <Input
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-white/10 border-white/15 text-white placeholder:text-slate-500
                             focus-visible:ring-white/20 focus-visible:border-white/30
                             hover:border-white/25 h-11"
                  required
                />
              </div>
              <Input
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                className="bg-white/10 border-white/15 text-white placeholder:text-slate-500
                           focus-visible:ring-white/20 focus-visible:border-white/30
                           hover:border-white/25 h-11"
                required
              />
              <Textarea
                name="message"
                placeholder="Your Message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                className="bg-white/10 border-white/15 text-white placeholder:text-slate-500
                           focus-visible:ring-white/20 focus-visible:border-white/30
                           hover:border-white/25 min-h-[120px]"
                required
              />
              <Button
                type="submit"
                size="lg"
                className="w-full h-12 text-[15px] font-semibold
                           bg-gradient-to-r from-brand-indigo to-brand-emerald
                           hover:from-brand-indigo/92 hover:to-brand-emerald/92
                           shadow-lg shadow-brand-indigo/30 hover:shadow-xl
                           hover:shadow-brand-indigo/35 transition-all duration-200"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
